/**
 * ErrorTable
 *
 * Reusable presentational component for displaying error rows in a
 * TanStack Table with sorting, per-column filtering, pagination, and
 * optional XLSX export. Used by VersionErrorDialog and OperationLogsDialog.
 */

import { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@loader/ui';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
} from 'lucide-react';

import type { ErrorRow } from './errorTableUtils';
import { downloadXlsx, formatErrorCount } from './errorTableUtils';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SortIcon({ column }: { column: { getIsSorted: () => false | 'asc' | 'desc' } }) {
  const sorted = column.getIsSorted();
  if (sorted === 'asc') return <ArrowUp className="ml-1 h-3.5 w-3.5" />;
  if (sorted === 'desc') return <ArrowDown className="ml-1 h-3.5 w-3.5" />;
  return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-40" />;
}

function ColumnHeader({ column, label }: { column: Column<ErrorRow>; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <Input
        placeholder={label}
        value={(column.getFilterValue() as string) ?? ''}
        onChange={(e) => column.setFilterValue(e.target.value)}
        className="h-7 text-xs"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        <SortIcon column={column} />
      </Button>
    </div>
  );
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columns: ColumnDef<ErrorRow>[] = [
  {
    accessorKey: 'field',
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: ({ column }) => <ColumnHeader column={column} label="Field" />,
    cell: ({ row }) => {
      const value = row.getValue<string>('field');
      return value === '-' ? (
        <span className="text-muted-foreground text-sm">-</span>
      ) : (
        <code className="bg-muted rounded px-1.5 py-0.5 text-xs font-semibold">{value}</code>
      );
    },
  },
  {
    accessorKey: 'type',
    size: 100,
    minSize: 80,
    maxSize: 130,
    header: ({ column }) => <ColumnHeader column={column} label="Type" />,
    cell: ({ row }) => {
      const value = row.getValue<string>('type');
      const isWarning = value === 'Warning';
      return (
        <span className={`text-sm font-medium whitespace-nowrap ${isWarning ? 'text-amber-600' : 'text-destructive'}`}>
          {value}
        </span>
      );
    },
  },
  {
    accessorKey: 'issue',
    header: ({ column }) => <ColumnHeader column={column} label="Details" />,
    cell: ({ row }) => (
      <span className="text-sm break-words">{row.getValue<string>('issue')}</span>
    ),
  },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface ErrorTableProps {
  rows: ErrorRow[];
  isTruncated?: boolean;
  exportFileName?: string;
  footerMessage?: string;
}

export function ErrorTable({ rows, isTruncated, exportFileName, footerMessage }: ErrorTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  const filteredCount = table.getFilteredRowModel().rows.length;

  const defaultFooter = `${formatErrorCount(rows.length, isTruncated)} found.${filteredCount !== rows.length ? ` ${filteredCount} shown.` : ''}`;

  return (
    <>
      {rows.length > 0 && (
        <div className="border-y h-[420px] overflow-y-auto">
          <Table className="table-fixed">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-muted/40 hover:bg-muted/40">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-9 px-4 py-1"
                      style={header.column.columnDef.size ? { width: `${header.column.getSize()}px` } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-4 align-top py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                    No matching errors.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs">
            {footerMessage ?? defaultFooter}
          </span>
          {exportFileName && rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => downloadXlsx(rows, exportFileName)}
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          )}
        </div>
        {table.getPageCount() > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.firstPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-muted-foreground mx-1 text-xs tabular-nums">
              {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!table.getCanNextPage()}
              onClick={() => table.lastPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
